import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {MenuMobileService} from "../../main/menu/menu-mobile.service";
import {toLazyImageItems} from './lazy-image-items';

@Component({
    selector: 'lazy-load-images',
    templateUrl: './lazy-load-images.component.html',
    styleUrls: ['./lazy-load-images.component.scss'],
    standalone: false
})
export class LazyLoadImagesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() imagesJsonStructure: any;
  @Input() imagesPath: string;
  newJsonStructure: any;
  hiddenMobileMenu = false;

  private observer: IntersectionObserver | undefined;
  private viewReady = false;

  constructor(
    private hiddenMobileService: MenuMobileService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.hiddenMobileService.hiddenMobileMenu.subscribe(hidden => {
      this.hiddenMobileMenu = hidden;
      this.scheduleObserve();
    });
    this.map();
  }

  ngOnChanges(): void {
    this.map();
    this.scheduleObserve();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.scheduleObserve();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private map(): void {
    if (this.imagesJsonStructure && this.imagesPath) {
      // item.basePath ?? imagesPath — patrz lazy-image-items.ts.
      this.newJsonStructure = toLazyImageItems(this.imagesJsonStructure, this.imagesPath);
    }
  }

  // Defer attaching the real image sources until each item nears the viewport.
  // Native loading="lazy" does not propagate to <source srcset> inside <picture>,
  // so all webp were fetched eagerly. IntersectionObserver fixes that.
  private scheduleObserve(): void {
    if (!this.viewReady || typeof IntersectionObserver === 'undefined') {
      // SSR / unsupported: fall back to loading everything (previous behaviour).
      if (this.viewReady) {
        this.host.nativeElement
          .querySelectorAll<HTMLElement>('.lazy-img-slot')
          .forEach(slot => this.loadSlot(slot));
      }
      return;
    }
    // Let Angular render the @for items first.
    setTimeout(() => this.observe(), 0);
  }

  private observe(): void {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadSlot(entry.target as HTMLElement);
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px 0px' });

    this.host.nativeElement
      .querySelectorAll<HTMLElement>('.lazy-img-slot:not(.lazy-loaded)')
      .forEach(slot => this.observer!.observe(slot));
  }

  private loadSlot(slot: HTMLElement): void {
    if (slot.classList.contains('lazy-loaded')) {
      return;
    }
    slot.querySelectorAll<HTMLImageElement>('img[data-src]').forEach(img => {
      const src = img.getAttribute('data-src');
      if (src) {
        img.src = src;
        img.removeAttribute('data-src');
      }
    });
    slot.querySelectorAll<HTMLSourceElement>('source[data-srcset]').forEach(source => {
      const srcset = source.getAttribute('data-srcset');
      if (srcset) {
        source.srcset = srcset;
        source.removeAttribute('data-srcset');
      }
    });
    slot.classList.add('lazy-loaded');
  }
}
